/**
 * The app shell's one handler for the shared answers (cowork/launch/apiSignals.ts):
 * - 429 cap_reached: one toast with the server's message (one per studio and unit at a time), and /v1/me again so
 *   every CapMeter shows the new count (studioErrors.ts showCapToast: the page that caught the error shares the toast);
 * - 403 clients_paused: the page on screen turns into the calm paused page when it is one a paused client may not use
 *   (`pauseBlocksPage`: the studios, Lykke, ordering a campaign); /v1/me is asked again, so every other page gets the
 *   paused banner from its answer;
 * - 403 feature_closed for the studio on screen: the calm "not switched on" page. Without a named feature (the older
 *   staff gate answer) only /v1/me is asked again, and the page follows what it says;
 * - 403 terms_required: the terms are asked again, which reopens the AI terms modal.
 * The page override clears when the person moves to another page.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { onApiSignal, installApiSignalObserver } from './apiSignals';
import { showCapToast } from './studioErrors';
import { CLIENT_TERMS_KEY } from './clientTerms';
import { useRefreshMe } from './useCoworkMe';
import { studioFeaturesForPage } from '@/cowork/clientFeatures';

export type StudioOverride = { state: 'closed' | 'paused'; path: string } | null;

export function useLaunchSignals(
  currentPage: string,
  pathname: string,
  apiBases: readonly string[],
  pauseBlocksPage = studioFeaturesForPage(currentPage).length > 0,
): 'closed' | 'paused' | null {
  const refreshMe = useRefreshMe();
  const qc = useQueryClient();
  const [override, setOverride] = useState<StudioOverride>(null);
  const basesKey = apiBases.join('|');

  useEffect(() => { installApiSignalObserver(basesKey.split('|')); }, [basesKey]);

  useEffect(() => onApiSignal((s) => {
    refreshMe();
    if (s.kind === 'cap_reached') {
      showCapToast(s.cap);
      return;
    }
    if (s.kind === 'terms_required') {
      void qc.invalidateQueries({ queryKey: CLIENT_TERMS_KEY });
      return;
    }
    if (s.kind === 'clients_paused') {
      if (pauseBlocksPage) setOverride({ state: 'paused', path: pathname });
      return;
    }
    const needs = studioFeaturesForPage(currentPage);
    if (s.feature && needs.includes(s.feature)) setOverride({ state: 'closed', path: pathname });
  }), [refreshMe, qc, currentPage, pathname, pauseBlocksPage]);

  return override && override.path === pathname ? override.state : null;
}
